import { FieldConfig, ValidationRule } from "../types/formTypes";

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
}

export interface TuitionFormValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

// ============================================================================
// HELPER: Result object factory (DRY principle)
// ============================================================================

const createValidResult = (): ValidationResult => ({
  isValid: true,
  errorMessage: "",
});

const createErrorResult = (errorMessage: string): ValidationResult => ({
  isValid: false,
  errorMessage,
});

// ============================================================================
// HELPER: Date utilities (eliminate date parsing duplication)
// ============================================================================

const isValidDateFormat = (dateStr: string): boolean => {
  const dateRegex = /^(0?\d|1[0-2])\/(0?\d|[12]\d|3[01])\/\d{4}$/;
  return dateRegex.test(dateStr);
};

const parseDate = (dateStr: string): Date | null => {
  if (!isValidDateFormat(dateStr)) return null;

  const parts = dateStr.split("/").map((p) => Number.parseInt(p, 10));
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;

  const [month, day, year] = parts;
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  // Validate the date is real (e.g., Feb 30 is invalid)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const formatDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// ============================================================================
// HELPER: Regex validation (eliminate regex pattern duplication)
// ============================================================================

const validateWithRegex = (
  strValue: string,
  regex: RegExp,
  errorMessage: string
): ValidationResult => {
  if (!strValue) return createValidResult();

  return regex.test(strValue)
    ? createValidResult()
    : createErrorResult(errorMessage);
};

// ============================================================================
// HELPER: Email validation rules (consolidate email logic)
// ============================================================================

const EMAIL_RULES = {
  hasAtSymbol: (email: string) => /@/.test(email),
  singleAtSymbol: (email: string) => (email.match(/@/g) || []).length === 1,
  noConsecutiveDots: (email: string) => !/\.\./.test(email),
  noInvalidChars: (email: string) => !/[^a-zA-Z0-9@._-]/.test(email),
  noLeadingDot: (email: string) => !email.startsWith("."),
  noDotBeforeAt: (email: string) => !/\.@/.test(email),
  matchesEmailRegex: (email: string) =>
    /^[a-zA-Z0-9][a-zA-Z0-9._-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/.test(
      email
    ),
};

const validateEmailRules = (email: string): boolean => {
  return Object.values(EMAIL_RULES).every((rule) => rule(email));
};

// ============================================================================
// HELPER: Date comparison
// ============================================================================

const compareDates = (
  date1: Date | null,
  date2: Date | null,
  comparison: "before" | "after" | "equal"
): boolean => {
  if (!date1 || !date2) return false;

  switch (comparison) {
    case "before":
      return date1 < date2;
    case "after":
      return date1 > date2;
    case "equal":
      return date1.getTime() === date2.getTime();
    default:
      return false;
  }
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateRequired = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue) {
    return createErrorResult(rule.message || "User Input required.");
  }
  return createValidResult();
};

const validateRequiredIf = (
  strValue: string,
  rule: ValidationRule,
  formData?: Record<string, any>,
  fieldName?: string
): ValidationResult => {
  if (rule.condition && formData && rule.condition(formData, fieldName)) {
    if (!strValue) {
      return createErrorResult(rule.message || "User Input required.");
    }
  }
  return createValidResult();
};

const validateEmail = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue) return createValidResult();

  if (!validateEmailRules(strValue)) {
    return createErrorResult(
      rule.message || "Invalid email address. Please enter a valid email."
    );
  }
  return createValidResult();
};

const validatePhone = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue) return createValidResult();

  if (strValue.includes("-")) {
    const phoneFormatRegex = /^\d{3}-\d{3}-\d{4}$/;
    return validateWithRegex(
      strValue,
      phoneFormatRegex,
      "Invalid phone number format. Please use XXX-XXX-XXXX format."
    );
  }

  const digitsOnly = strValue.replaceAll(/\D/g, "");
  if (digitsOnly.length !== 10 || strValue !== digitsOnly) {
    return createErrorResult(
      rule.message || "Invalid phone number. Please enter a 10-digit phone number."
    );
  }
  return createValidResult();
};

const validateUrl = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  const urlRegex =
    /^(https?:\/\/)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(\/[^\s]*)?$/i;
  return validateWithRegex(
    strValue,
    urlRegex,
    rule.message || "Invalid URL."
  );
};

const validateMinLength = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue || rule.value === undefined) return createValidResult();

  if (strValue.length < rule.value) {
    return createErrorResult(
      rule.message || `Must be at least ${rule.value} characters.`
    );
  }
  return createValidResult();
};

const validateMaxLength = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue || rule.value === undefined) return createValidResult();

  if (strValue.length > rule.value) {
    return createErrorResult(
      rule.message || `Must not exceed ${rule.value} characters.`
    );
  }
  return createValidResult();
};

const validateMin = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue || rule.value === undefined) return createValidResult();

  const numValue = Number(strValue);
  if (!Number.isNaN(numValue) && numValue < rule.value) {
    return createErrorResult(rule.message || "Enter a valid number.");
  }
  return createValidResult();
};

const validateMax = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue || rule.value === undefined) return createValidResult();

  const numValue = Number(strValue);
  if (!Number.isNaN(numValue) && numValue > rule.value) {
    return createErrorResult(
      rule.message || `Value must not exceed ${rule.value}.`
    );
  }
  return createValidResult();
};

const validateMinDate = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue || !rule.value) return createValidResult();

  const selectedDate = parseDate(strValue);
  if (!selectedDate) return createValidResult();

  const [minYear, minMonth, minDay] = String(rule.value)
    .split("-")
    .map((part) => Number.parseInt(part, 10));
  const minDate = new Date(minYear, minMonth - 1, minDay);
  minDate.setHours(0, 0, 0, 0);

  if (selectedDate < minDate) {
    return createErrorResult(
      rule.message || `Date must be on or after ${formatDate(minDate)}.`
    );
  }
  return createValidResult();
};

const validateMaxDate = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue || !rule.value) return createValidResult();

  const selectedDate = parseDate(strValue);
  if (!selectedDate) return createValidResult();

  const [maxYear, maxMonth, maxDay] = String(rule.value)
    .split("-")
    .map((part) => Number.parseInt(part, 10));
  const maxDate = new Date(maxYear, maxMonth - 1, maxDay);
  maxDate.setHours(0, 0, 0, 0);

  if (selectedDate > maxDate) {
    return createErrorResult(
      rule.message || `Date must be on or before ${formatDate(maxDate)}.`
    );
  }
  return createValidResult();
};

const validateDateBefore = (
  strValue: string,
  rule: ValidationRule,
  formData?: Record<string, any>
): ValidationResult => {
  if (!strValue || !rule.compareField || !formData) {
    return createValidResult();
  }

  const compareValue = formData[rule.compareField];
  if (!compareValue) {
    return createValidResult();
  }

  const date1 = parseDate(strValue);
  const date2 = parseDate(String(compareValue));

  if (!date1 || !date2) {
    return createValidResult();
  }

  if (compareDates(date1, date2, "before")) {
    return createErrorResult(
      rule.message || `Date must be later than ${formatDate(date2)}.`
    );
  }

  return createValidResult();
};

const validateDateFormat = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue) return createValidResult();

  if (!isValidDateFormat(strValue)) {
    return createErrorResult(
      rule.message ||
        "All date fields must follow the correct format (MM/DD/YYYY)."
    );
  }

  const parsedDate = parseDate(strValue);
  if (!parsedDate) {
    return createErrorResult(
      rule.message ||
        "All date fields must follow the correct format (MM/DD/YYYY)."
    );
  }

  return createValidResult();
};

const validatePattern = (
  strValue: string,
  rule: ValidationRule
): ValidationResult => {
  if (!strValue || !rule.value) return createValidResult();

  const regex = new RegExp(rule.value);
  return validateWithRegex(
    strValue,
    regex,
    rule.message || "Invalid format."
  );
};

const validateCustom = (
  value: any,
  rule: ValidationRule,
  formData?: Record<string, any>
): ValidationResult => {
  if (!rule.validator) return createValidResult();

  if (!rule.validator(value, formData)) {
    return createErrorResult(rule.message || "Validation failed.");
  }
  return createValidResult();
};

// ============================================================================
// VALIDATION FUNCTION MAP
// ============================================================================

const validationFunctionMap: Record<
  string,
  (
    strValue: string,
    rule: ValidationRule,
    formData?: Record<string, any>,
    fieldName?: string
  ) => ValidationResult
> = {
  required: validateRequired,
  requiredIf: validateRequiredIf,
  email: validateEmail,
  phone: validatePhone,
  url: validateUrl,
  minLength: validateMinLength,
  maxLength: validateMaxLength,
  min: validateMin,
  max: validateMax,
  minDate: validateMinDate,
  maxDate: validateMaxDate,
  dateBefore: validateDateBefore,
  dateFormat: validateDateFormat,
  pattern: validatePattern,
  custom: validateCustom,
};

// ============================================================================
// MAIN VALIDATION FUNCTIONS
// ============================================================================

const validateRule = (
  value: any,
  rule: ValidationRule,
  formData?: Record<string, any>,
  fieldName?: string
): ValidationResult => {
  const strValue =
    value !== undefined && value !== null ? String(value).trim() : "";

  const validationFn = validationFunctionMap[rule.type];

  if (validationFn) {
    return validationFn(strValue, rule, formData, fieldName);
  }

  return createValidResult();
};

export const validateField = (
  value: any,
  validationRules: ValidationRule[],
  formData?: Record<string, any>
): ValidationResult => {
  for (const rule of validationRules) {
    const result = validateRule(value, rule, formData);
    if (!result.isValid) {
      return result;
    }
  }

  return createValidResult();
};

/**
 * Tuition-specific validation function that validates ALL fields and collects ALL errors
 * Unlike the citizenship validation engine, this does not stop at the first error
 */
export const validateTuitionForm = (
  fields: FieldConfig[],
  formData: Record<string, any>
): TuitionFormValidationResult => {
  const allErrors: Record<string, string> = {};

  // Validate each field and collect all errors
  for (const field of fields) {
    if (!field.validations || field.validations.length === 0) continue;

    const value = formData[field.name];

    // Check each validation rule for this field
    for (const rule of field.validations) {
      const result = validateRule(value, rule, formData, field.name);

      // If validation fails, add to errors (only first error per field)
      if (!result.isValid && !allErrors[field.name]) {
        allErrors[field.name] = result.errorMessage;
        break; // Move to next field after first error for this field
      }
    }
  }

  return {
    isValid: Object.keys(allErrors).length === 0,
    errors: allErrors,
  };
};

export default {
  validateField,
  validateTuitionForm,
};